import { useCallback, useEffect, useRef, useState } from 'react';
import { pushToast, type ToastTone } from '@/store/slices/uiSlice';
import { useAppDispatch } from '@/store/store';

/** Raise a toast without wiring dispatch at every call site. */
export function useToast() {
  const dispatch = useAppDispatch();

  return useCallback(
    (title: string, options?: { description?: string; tone?: ToastTone }) => {
      dispatch(pushToast({ title, ...options }));
    },
    [dispatch]
  );
}

/** Delays a fast-changing value — used for search boxes so every keystroke is not a request. */
export function useDebounced<T>(value: T, delayMs = 350): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);

  return debounced;
}

/** Sets document.title, restoring the previous one on unmount. */
export function useDocumentTitle(title: string): void {
  useEffect(() => {
    const previous = document.title;
    document.title = `${title} · HireOrbit`;
    return () => {
      document.title = previous;
    };
  }, [title]);
}

/** True only after the first render — for skipping effects that should not run on mount. */
export function useIsMounted(): boolean {
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
  }, []);
  return mounted.current;
}
